import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { getPostsBySlugs } from "@/data/blogPosts";

interface RelatedArticlesProps {
  /** Blog post slugs to feature, in display order. Missing slugs are skipped. */
  slugs: string[];
  heading?: string;
  intro?: string;
  /** Tailwind background class so the section stripes cleanly with its neighbors. */
  tone?: string;
}

export function RelatedArticles({
  slugs,
  heading = "From the Help Hub",
  intro = "Helpful guides and answers from our roofing and siding team to help you plan your next project with confidence.",
  tone = "bg-muted",
}: RelatedArticlesProps) {
  const posts = getPostsBySlugs(slugs);

  if (posts.length === 0) return null;

  return (
    <section className={`py-16 px-4 ${tone}`} data-testid="section-related-articles">
      <div className="container mx-auto max-w-5xl">
        <h2 className="text-3xl font-serif font-bold text-foreground mb-3">
          {heading}
        </h2>
        <p className="text-muted-foreground mb-8 max-w-3xl">{intro}</p>
        <div className={`grid grid-cols-1 ${posts.length === 4 ? "md:grid-cols-2" : "md:grid-cols-3"} gap-5`}>
          {posts.map((post) => (
            <Link
              key={post.slug}
              href={`/${post.slug}/`}
              className="flex flex-col p-6 bg-card border border-border rounded-lg hover:border-primary transition-colors"
              data-testid={`link-article-${post.slug}`}
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">
                {post.category}
              </span>
              <h3 className="font-bold text-foreground text-lg mb-2">
                {post.title}
              </h3>
              <p className="text-muted-foreground text-sm mb-4 flex-grow">
                {post.excerpt}
              </p>
              <span className="text-primary text-sm font-semibold inline-flex items-center gap-1">
                Read article
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link
            href="/blog/"
            className="text-primary font-bold hover:underline inline-flex items-center gap-2"
            data-testid="link-learning-center-all"
          >
            Visit the Help Hub
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
